export const SKILL_API_BASE = '/backend/common/api/v2';
export const SKILL_AI_STATISTICS_API_BASE = '/backend/admin/skill-learning/ai';

import {
  SkillSpecializationDeactivateParams,
  SkillTrainingRequirementDeleteParams,
  SkillTrainingRequirementScope,
} from '../models/skill-learning.model';

export const SKILL_DOCUMENT_FORM_FIELD = 'file';

export const SKILL_SPECIALIZATIONS_API = {
  BASE: `${SKILL_API_BASE}/skill-specializations`,
  ADMIN_LIST: (departmentId?: string) => departmentId
    ? `${SKILL_API_BASE}/skill-specializations?departmentId=${encodeURIComponent(departmentId)}`
    : `${SKILL_API_BASE}/skill-specializations`,
  AVAILABLE: (departmentId?: string) => departmentId
    ? `${SKILL_API_BASE}/skill-specializations/available?departmentId=${encodeURIComponent(departmentId)}`
    : `${SKILL_API_BASE}/skill-specializations/available`,
  CREATION_PERMISSIONS: `${SKILL_API_BASE}/skill-specializations/creation-permissions`,
  BY_ID: (id: string) => `${SKILL_API_BASE}/skill-specializations/${id}`,
  BY_ID_FOR_DEPARTMENT: (id: string, departmentId: string) =>
    `${SKILL_API_BASE}/skill-specializations/${id}?departmentId=${encodeURIComponent(departmentId)}`,
  DEACTIVATE: (id: string, params: SkillSpecializationDeactivateParams) => {
    const department = params.departmentId
      ? `&departmentId=${encodeURIComponent(params.departmentId)}` : '';
    return `${SKILL_API_BASE}/skill-specializations/${id}`
      + `?expectedRevision=${params.expectedRevision}${department}`;
  },
  STRUCTURE: (id: string) => `${SKILL_API_BASE}/skill-specializations/${id}/structure`,
  REQUIREMENT: (id: string, scope: SkillTrainingRequirementScope) =>
    `${SKILL_API_BASE}/skill-specializations/${id}/requirements/${scope}`,
  REQUIREMENT_DELETE: (
    id: string,
    scope: SkillTrainingRequirementScope,
    params: SkillTrainingRequirementDeleteParams,
  ) => {
    const department = params.departmentId
      ? `&departmentId=${encodeURIComponent(params.departmentId)}` : '';
    const responseDepartment = params.responseDepartmentId
      ? `&responseDepartmentId=${encodeURIComponent(params.responseDepartmentId)}` : '';
    return `${SKILL_API_BASE}/skill-specializations/${id}/requirements/${scope}`
      + `?expectedRevision=${params.expectedRevision}${department}${responseDepartment}`;
  },
  FILL_TASK: (id: string) => `${SKILL_API_BASE}/skill-specializations/${id}/fill-task`,
  PROBATION_REQUIREMENT: (id: string) =>
    `${SKILL_API_BASE}/skill-specializations/${id}/probation-requirement`,
  CURATORS: (id: string) => `${SKILL_API_BASE}/skill-specializations/${id}/curators`,
  CURATOR_BY_ID: (id: string, curatorId: string, departmentId?: string) => {
    const department = departmentId
      ? `?departmentId=${encodeURIComponent(departmentId)}` : '';
    return `${SKILL_API_BASE}/skill-specializations/${id}/curators/${curatorId}${department}`;
  },
  THEORY_LEVEL: (id: string, level: number) =>
    `${SKILL_API_BASE}/skill-specializations/${id}/levels/${level}/theory`,
  THEORY_TESTS_GENERATE: (id: string, level: number) =>
    `${SKILL_API_BASE}/skill-specializations/${id}/levels/${level}/tests/generate`,
  THEORY_DIRECTIONS_PROMPT: (id: string, level: number) =>
    `${SKILL_API_BASE}/skill-specializations/${id}/levels/${level}/directions-prompt`,
  PRACTICE_LEVEL: (id: string, level: number) =>
    `${SKILL_API_BASE}/skill-specializations/${id}/levels/${level}/practice`,
  LEVEL_DOCUMENT: (id: string, level: number) =>
    `${SKILL_API_BASE}/skill-specializations/${id}/levels/${level}/document`,
  LEVEL_DOCUMENT_UPLOAD: (id: string, level: number, expectedRevision: number) =>
    `${SKILL_API_BASE}/skill-specializations/${id}/levels/${level}/document`
      + `?expectedRevision=${expectedRevision}`,
  LEVEL_DOCUMENT_PREVIEW_LINK: (id: string, level: number) =>
    `${SKILL_API_BASE}/skill-specializations/${id}/levels/${level}/document/preview-link`,
  BY_GROUP: (groupId: string) => `${SKILL_API_BASE}/skill-specializations/by-group/${groupId}`,
  MY_CURATED: `${SKILL_API_BASE}/skill-specializations/my-curated`,
};

export const SKILL_DOCUMENT_PREVIEWS_API = {
  BY_TOKEN: (token: string, fileName: string) =>
    `${SKILL_API_BASE}/skill-document-previews/${encodeURIComponent(token)}/${encodeURIComponent(fileName)}`,
};

export const SKILL_PROGRESS_API = {
  DEPARTMENTS: `${SKILL_API_BASE}/skill-progress/departments`,
  DEPARTMENT_STAR_MAP: (groupId: string) =>
    `${SKILL_API_BASE}/skill-progress/groups/${groupId}/star-map`,
  BY_SPECIALIZATION: (specializationId: string) =>
    `${SKILL_API_BASE}/skill-progress/${specializationId}`,
  TOPIC_TOGGLE: (specializationId: string, topicIndex: number) =>
    `${SKILL_API_BASE}/skill-progress/${specializationId}/topics/${topicIndex}/toggle`,
  BY_USER: (specializationId: string, userId: string) =>
    `${SKILL_API_BASE}/skill-progress/${specializationId}/user/${userId}`,
  SCORE_STATUS: `${SKILL_API_BASE}/skill-progress/score-status`,
  WEIGHT_SUM: `${SKILL_API_BASE}/skill-progress/weight-sum`,
  USER_WEIGHT_SUM: (userId: string) => `${SKILL_API_BASE}/skill-progress/users/${userId}/weight-sum`,
};

export const SKILL_CHAT_API = {
  MESSAGE: (specializationId: string) =>
    `${SKILL_API_BASE}/skill-chat/${specializationId}/message`,
  HISTORY: (specializationId: string) =>
    `${SKILL_API_BASE}/skill-chat/${specializationId}/history`,
  CHAT: (specializationId: string) =>
    `${SKILL_API_BASE}/skill-chat/${specializationId}`,
};

export const SKILL_TESTS_API = {
  GENERATE: (specializationId: string, level: number = 1) =>
    `${SKILL_API_BASE}/skill-tests/${specializationId}/generate?level=${level}`,
  SUBMIT: (specializationId: string, level: number = 1) =>
    `${SKILL_API_BASE}/skill-tests/${specializationId}/submit?level=${level}`,
  COOLDOWN: (specializationId: string, level: number = 1) =>
    `${SKILL_API_BASE}/skill-tests/${specializationId}/cooldown?level=${level}`,
};

export const SKILL_SETTINGS_API = {
  GENERAL: `${SKILL_API_BASE}/skill-learning/settings`,
};

export const SKILL_AI_STATISTICS_API = {
  SUMMARY: `${SKILL_AI_STATISTICS_API_BASE}/statistics/summary`,
  TIMESERIES: `${SKILL_AI_STATISTICS_API_BASE}/statistics/timeseries`,
  TOP_USERS: `${SKILL_AI_STATISTICS_API_BASE}/statistics/top-users`,
  TOP_SPECIALIZATIONS: `${SKILL_AI_STATISTICS_API_BASE}/statistics/top-specializations`,
  LOGS: `${SKILL_AI_STATISTICS_API_BASE}/logs`,
  BUDGET_STATUS: `${SKILL_AI_STATISTICS_API_BASE}/budget-status`,
  STANDARD_QUESTIONS_STATISTICS: `${SKILL_AI_STATISTICS_API_BASE}/standard-questions/statistics`,
  LEARNING_POPULARITY_STATISTICS: `${SKILL_AI_STATISTICS_API_BASE}/learning-popularity/statistics`,
  TRANSLATION_COVERAGE_STATISTICS: `${SKILL_AI_STATISTICS_API_BASE}/translations/statistics`,
};

export const SKILL_PRACTICE_API = {
  START: (specializationId: string, level: number, taskType?: string) =>
    `${SKILL_API_BASE}/skill-practice/${specializationId}/levels/${level}/start${taskType ? '?taskType=' + taskType : ''}`,
  TASK: (specializationId: string, level: number, taskType?: string) =>
    `${SKILL_API_BASE}/skill-practice/${specializationId}/levels/${level}/task${taskType ? '?taskType=' + taskType : ''}`,
};

export const SKILL_REQUESTS_API = {
  BASE: `${SKILL_API_BASE}/skill-requests`,
  BY_ID: (id: string) => `${SKILL_API_BASE}/skill-requests/${id}`,
  MY: `${SKILL_API_BASE}/skill-requests/my`,
  APPROVE: (id: string) => `${SKILL_API_BASE}/skill-requests/${id}/approve`,
  REJECT: (id: string) => `${SKILL_API_BASE}/skill-requests/${id}/reject`,
};

export const SKILL_GROUPS_API = {
  BASE: `${SKILL_API_BASE}/skill-groups`,
  BY_ID: (groupId: string) => `${SKILL_API_BASE}/skill-groups/${groupId}`,
  GROUP_USERS: (groupId: string) =>
    `${SKILL_API_BASE}/skill-groups/${groupId}/users`,
  AVAILABLE_CURATORS: (groupId: string, specializationId: string) =>
    `${SKILL_API_BASE}/skill-groups/${groupId}/available-curators?specializationId=${specializationId}`,
};



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/constants/skill-api.constants.ts